import * as ReactHelmetAsync from 'react-helmet-async';
const Helmet = ReactHelmetAsync.Helmet || (ReactHelmetAsync as any).default?.Helmet;
import { Link, useSearchParams } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { PageHero } from '@/components/sections/PageHero';
import { loadBlogPosts, getAllLabels } from '@/utils/blogParser.ts';
import { resolveImagePath } from '@/utils/imageMap.ts';

const allPosts = loadBlogPosts();
const allLabels = getAllLabels(allPosts);

export function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeLabel = searchParams.get('label') || '';

  const filteredPosts = activeLabel
    ? allPosts.filter((p) => p.labels.includes(activeLabel))
    : allPosts;

  function handleLabelClick(label: string) {
    if (label === activeLabel) {
      setSearchParams({});
    } else {
      setSearchParams({ label });
    }
  }

  return (
    <>
      <Helmet>
        <title>Blog | Total Tailwheel</title>
        <meta
          name="description"
          content="News, tips, and stories from Total Tailwheel - tailwheel flight training at Minute Man Air Field near Boston."
        />
        <meta property="og:title" content="Blog | Total Tailwheel" />
        <meta
          property="og:description"
          content="News, tips, and stories from Total Tailwheel."
        />
        <link rel="canonical" href="https://totaltailwheel.com/blog" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navigation />

        <PageHero title="Blog" />

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-5xl">
            {/* Label filter pills */}
            {allLabels.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-10 justify-center max-w-xl mx-auto">
                {allLabels.map((label) => (
                  <button
                    key={label}
                    onClick={() => handleLabelClick(label)}
                    className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                      label === activeLabel
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                {activeLabel && (
                  <button
                    onClick={() => setSearchParams({})}
                    className="text-sm px-3 py-1 rounded-full border border-gray-300 bg-white text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            )}

            {/* Post list */}
            {filteredPosts.length === 0 ? (
              <p className="text-center text-lg text-gray-500">
                No posts found{activeLabel ? ` for "${activeLabel}"` : ''}.
              </p>
            ) : (
              <div className="space-y-8">
                {filteredPosts.map((post) => {
                  const formattedDate = new Date(post.date + 'T00:00:00').toLocaleDateString(
                    'en-US',
                    { year: 'numeric', month: 'long', day: 'numeric' },
                  );

                  return (
                    <Link
                      key={post.slug}
                      to={`/blog/${post.slug}`}
                      className="flex flex-col sm:flex-row gap-5 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
                    >
                      {/* Thumbnail */}
                      {post.image && (
                        <div className="sm:w-48 sm:min-w-[12rem] h-48 sm:h-auto flex-shrink-0">
                          <img
                            src={resolveImagePath(post.thumbnail || post.image)}
                            alt={post.imageAlt || post.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* Text */}
                      <div className="p-5 flex flex-col justify-center">
                        <p className="text-xs text-gray-400 mb-1">{formattedDate}</p>
                        <h2 className="text-xl font-heading font-bold text-gray-900 group-hover:text-primary transition-colors mb-2">
                          {post.title}
                        </h2>
                        <p className="text-gray-600 text-sm leading-relaxed mb-3">
                          {post.snippet}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {post.labels.map((label) => (
                            <span
                              key={label}
                              className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
